import React, { useState } from "react";
import PropTypes from "prop-types";
import { Alert, Box, CircularProgress, Paper, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FormInput from "../Common/FormInputs/MuiTextField";
import AppButton from "../Common/Button/Button";
import {
  PageHeading,
  SectionSubHeading,
} from "../Common/Typography/AppTypography";
import { ACTION_BUTTON_HEIGHT } from "../../constants/uiConstants";
import validateSmartRoomSearch from "./smartRoomSearchValidation";

const SearchForm = ({
  homeId,
  spaceId,
  spaceOptions,
  spacesLoading,
  spacesError,
  onHomeIdChange,
  onHomeIdBlur,
  onSpaceIdChange,
  onSubmit,
  loading,
  error,
}) => {
  const [fieldErrors, setFieldErrors] = useState({});

  const clearFieldError = (name) => {
    setFieldErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleHomeIdChange = (event) => {
    clearFieldError("homeId");
    clearFieldError("spaceId");
    onHomeIdChange?.(event);
  };

  const handleSpaceIdChange = (event) => {
    clearFieldError("spaceId");
    onSpaceIdChange?.(event);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = validateSmartRoomSearch({ homeId, spaceId });
    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }
    onSubmit?.();
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      if (event.target?.name === "homeId") {
        onHomeIdBlur?.();
        return;
      }
      handleSubmit(event);
    }
  };

  const spaceSelectOptions = [
    { value: "", label: spacesLoading ? "Loading spaces…" : "Select Space" },
    ...spaceOptions.map((space) => ({
      value: space.spaceId,
      label: space.label || `${space.spaceName} (${space.spaceId})`,
    })),
  ];

  const spaceDisabled =
    loading || spacesLoading || !spaceOptions.length;

  return (
    <Paper
      elevation={0}
      variant="outlined"
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: 2,
        maxWidth: 720,
        mx: "auto",
      }}
    >
      <PageHeading sx={{ mb: 0.5 }}>Smart Room Dashboard</PageHeading>
      <SectionSubHeading sx={{ mb: 2.5 }}>
        Enter a Home ID to load its spaces, then select a Space to view indoor
        and outdoor environmental conditions for that space.
      </SectionSubHeading>

      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr auto" },
          gap: 2,
          alignItems: "start",
        }}
      >
        <FormInput
          id="smart-room-home-id"
          name="homeId"
          label="Home ID"
          placeholder="Enter Home ID"
          value={homeId}
          onChange={handleHomeIdChange}
          onBlur={() => onHomeIdBlur?.()}
          onKeyDown={handleKeyDown}
          required
          disabled={loading}
          error={Boolean(fieldErrors.homeId)}
          helperText={fieldErrors.homeId || ""}
          touched="true"
          fullWidth
        />
        <Box sx={{ position: "relative" }}>
          <FormInput
            id="smart-room-space-id"
            name="spaceId"
            type="select"
            label="Space ID"
            value={spaceId}
            onChange={handleSpaceIdChange}
            required
            disabled={spaceDisabled}
            error={Boolean(fieldErrors.spaceId)}
            helperText={
              fieldErrors.spaceId ||
              (!homeId.trim()
                ? "Enter Home ID first"
                : spacesLoading
                  ? "Loading spaces…"
                  : !spaceOptions.length
                    ? "Spaces will load after Home ID is entered"
                    : "")
            }
            touched="true"
            fullWidth
            options={spaceSelectOptions}
          />
          {spacesLoading ? (
            <CircularProgress
              size={18}
              sx={{ position: "absolute", right: 36, top: 18 }}
            />
          ) : null}
        </Box>
        <AppButton
          type="submit"
          variant="primary"
          loading={loading}
          disabled={loading || spacesLoading || !spaceId}
          startIcon={<SearchIcon />}
          sx={{
            height: ACTION_BUTTON_HEIGHT,
            minHeight: ACTION_BUTTON_HEIGHT,
            whiteSpace: "nowrap",
            mt: { xs: 0, sm: "0px" },
          }}
        >
          Load Dashboard
        </AppButton>
      </Box>

      {spacesError ? (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {spacesError}
        </Alert>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      ) : null}

      {spaceOptions.length > 0 && spaceId ? (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: "block" }}>
          Selected space will drive Indoor, Outdoor, and comparison views.
        </Typography>
      ) : null}
    </Paper>
  );
};

SearchForm.propTypes = {
  homeId: PropTypes.string.isRequired,
  spaceId: PropTypes.string.isRequired,
  spaceOptions: PropTypes.arrayOf(
    PropTypes.shape({
      spaceId: PropTypes.string.isRequired,
      spaceName: PropTypes.string,
      label: PropTypes.string,
    })
  ),
  spacesLoading: PropTypes.bool,
  spacesError: PropTypes.string,
  onHomeIdChange: PropTypes.func.isRequired,
  onHomeIdBlur: PropTypes.func,
  onSpaceIdChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
};

SearchForm.defaultProps = {
  spaceOptions: [],
  spacesLoading: false,
  spacesError: "",
};

export default SearchForm;
